import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import logo from "../assets/images/logo.webp";

type GuideStep = "login" | "signup" | null;

/* ═══════════════════════════════════════════════════
   INJECT STYLES
═══════════════════════════════════════════════════ */
const injectStyles = () => {
  if (document.getElementById("slm-header-styles")) return;
  const s = document.createElement("style");
  s.id = "slm-header-styles";
  s.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    /* ── Keyframes ── */
    @keyframes slideInBot {
      from { opacity:0; transform:translateY(-10px) scale(0.95); }
      to   { opacity:1; transform:translateY(0)     scale(1);    }
    }
    @keyframes fadeInOverlay { from{opacity:0} to{opacity:1} }
    @keyframes botCursor     { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes speakBar      { 0%,100%{transform:scaleY(1)} 50%{transform:scaleY(1.8)} }
    @keyframes pulseDot      { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes glowBtn       { 0%,100%{box-shadow:0 0 0 0 rgba(192,0,26,0.6)} 50%{box-shadow:0 0 0 8px rgba(192,0,26,0)} }
    @keyframes slideDown      { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }

    @keyframes botBobSvg { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-2.5px)} }
    @keyframes floatHeartL1 {
      0%  { transform:translate(0,0)         scale(1);    opacity:1; }
      100%{ transform:translate(-14px,-54px) scale(0.18); opacity:0; }
    }
    @keyframes floatHeartL2 {
      0%  { transform:translate(0,0)        scale(0.82); opacity:0.9; }
      100%{ transform:translate(-4px,-58px) scale(0.12); opacity:0; }
    }
    @keyframes floatHeartL3 {
      0%  { transform:translate(0,0)       scale(0.68); opacity:0.8; }
      100%{ transform:translate(9px,-50px) scale(0.22); opacity:0; }
    }
    @keyframes floatHeartR1 {
      0%  { transform:translate(0,0)        scale(1);    opacity:1; }
      100%{ transform:translate(14px,-54px) scale(0.18); opacity:0; }
    }
    @keyframes floatHeartR2 {
      0%  { transform:translate(0,0)       scale(0.82); opacity:0.9; }
      100%{ transform:translate(4px,-58px) scale(0.12); opacity:0; }
    }
    @keyframes floatHeartR3 {
      0%  { transform:translate(0,0)        scale(0.68); opacity:0.8; }
      100%{ transform:translate(-9px,-50px) scale(0.22); opacity:0; }
    }

    /* ── Bot SVG classes ── */
    .slm-bot-bob { animation: botBobSvg 3s ease-in-out infinite; }
    .slm-hl1 { animation: floatHeartL1 2s 0.0s  ease-out infinite; transform-origin: 23px 27px; }
    .slm-hl2 { animation: floatHeartL2 2s 0.65s ease-out infinite; transform-origin: 23px 27px; }
    .slm-hl3 { animation: floatHeartL3 2s 1.3s  ease-out infinite; transform-origin: 23px 27px; }
    .slm-hr1 { animation: floatHeartR1 2s 0.2s  ease-out infinite; transform-origin: 49px 27px; }
    .slm-hr2 { animation: floatHeartR2 2s 0.85s ease-out infinite; transform-origin: 49px 27px; }
    .slm-hr3 { animation: floatHeartR3 2s 1.5s  ease-out infinite; transform-origin: 49px 27px; }

    /* ══ HEADER WRAPPER ══ */
    .slm-header {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 9100;
      background: #ffffff;
      border-bottom: 1px solid rgba(0,0,0,0.07);
      box-shadow: 0 1px 12px rgba(0,0,0,0.06);
      height: 72px;
      display: flex;
      align-items: center;
    }
    .slm-header.scrolled {
      box-shadow: 0 2px 20px rgba(0,0,0,0.10);
    }

    .slm-header-inner {
      display: flex;
      align-items: center;
      width: 100%;
      gap: 0;
    }

    /* ── Logo ── */
    .slm-logo {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      text-decoration: none;
      margin-right: 32px;
    }
    .slm-logo img {
      height: 52px;
      width: auto;
      object-fit: contain;
    }

    /* ── Nav links (center) ── */
    .slm-nav {
      display: flex;
      align-items: center;
      gap: 4px;
      list-style: none;
      margin: 0;
      padding: 0;
      flex: 1;
    }
    .slm-nav-link {
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      font-weight: 500;
      color: #333;
      text-decoration: none;
      padding: 6px 14px;
      border-radius: 8px;
      transition: color .15s, background .15s;
      white-space: nowrap;
    }
    .slm-nav-link:hover  { color: #c0001a; background: rgba(192,0,26,.05); }
    .slm-nav-link.active { color: #c0001a; font-weight: 600; }

    /* ── Nav dropdown (Location) ── */
    .slm-nav-dropdown { position: relative; list-style: none; }
    .slm-nav-dropdown-toggle {
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      font-weight: 500;
      color: #333;
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px 14px;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: color .15s, background .15s;
      white-space: nowrap;
    }
    .slm-nav-dropdown-toggle:hover,
    .slm-nav-dropdown-toggle.active { color: #c0001a; background: rgba(192,0,26,.05); }
    .slm-nav-caret { font-size: 11px; transition: transform .2s ease; }
    .slm-nav-caret.open { transform: rotate(180deg); }

    .slm-nav-dropdown-menu {
      position: absolute;
      top: calc(100% + 8px);
      left: 0;
      min-width: 190px;
      background: #fff;
      border-radius: 12px;
      border: 1px solid rgba(0,0,0,0.07);
      box-shadow: 0 12px 32px rgba(0,0,0,0.12);
      padding: 8px;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-6px);
      transition: opacity .18s ease, transform .18s ease, visibility .18s ease;
      z-index: 9150;
    }
    .slm-nav-dropdown:hover .slm-nav-dropdown-menu,
    .slm-nav-dropdown-menu.open {
      opacity: 1; visibility: visible; transform: translateY(0);
    }
    .slm-nav-dropdown-item {
      display: block;
      padding: 9px 12px;
      border-radius: 8px;
      font-family: 'Inter', sans-serif;
      font-size: 14.5px;
      font-weight: 500;
      color: #333;
      text-decoration: none;
      transition: background .15s, color .15s;
    }
    .slm-nav-dropdown-item:hover,
    .slm-nav-dropdown-item.active { background: rgba(192,0,26,.06); color: #c0001a; }

    /* ── Mobile Location dropdown ── */
    .slm-mobile-dropdown-toggle {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 15px;
      font-family: 'Inter', sans-serif;
      color: #333;
      background: none;
      border: none;
      cursor: pointer;
      text-align: left;
    }
    .slm-mobile-dropdown-toggle:hover,
    .slm-mobile-dropdown-toggle.active { background: rgba(192,0,26,.06); color: #c0001a; }
    .slm-mobile-dropdown-toggle i { font-size: 12px; transition: transform .2s ease; }
    .slm-mobile-dropdown-toggle i.open { transform: rotate(180deg); }
    .slm-mobile-dropdown-menu {
      display: flex;
      flex-direction: column;
      padding-left: 14px;
    }

    /* ── Auth buttons (right) ── */
    .slm-auth {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-shrink: 0;
    }

    .slm-login-btn {
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      font-weight: 500;
      color: #333;
      text-decoration: none;
      padding: 7px 14px;
      border-radius: 8px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: color .15s;
      white-space: nowrap;
    }
    .slm-login-btn:hover { color: #c0001a; }
    .slm-login-btn i { font-size: 14px; }

    .slm-signup-btn {
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      font-weight: 700;
      color: #fff !important;
      text-decoration: none;
      padding: 9px 24px;
      border-radius: 999px;
      background: #c0001a;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      transition: background .15s, box-shadow .15s, transform .15s;
      white-space: nowrap;
      box-shadow: 0 3px 12px rgba(192,0,26,0.28);
    }
    .slm-signup-btn:hover {
      background: #a80017;
      box-shadow: 0 5px 18px rgba(192,0,26,0.42);
      transform: translateY(-1px);
      color: #fff !important;
    }
    .slm-signup-btn:active { transform: scale(.97); }

    .slm-dashboard-btn {
      font-family: 'Inter', sans-serif;
      font-size: 15px;
      font-weight: 700;
      color: #fff !important;
      text-decoration: none;
      padding: 9px 22px;
      border-radius: 999px;
      background: linear-gradient(135deg, #c0001a, #8b0000);
      display: inline-flex;
      align-items: center;
      gap: 7px;
      transition: box-shadow .15s, transform .15s;
      white-space: nowrap;
      box-shadow: 0 3px 12px rgba(192,0,26,0.3);
    }
    .slm-dashboard-btn:hover { transform: translateY(-1px); box-shadow: 0 5px 18px rgba(192,0,26,0.4); color: #fff !important; }

    /* ── Bot anchor wrapper ── */
    .slm-anchor {
      position: relative;
      display: inline-flex;
      align-items: center;
    }
    .slm-anchor.glow .slm-login-btn,
    .slm-anchor.glow .slm-signup-btn {
      animation: glowBtn 1.8s ease-in-out infinite;
    }

    /* ══ BOT POPUP ══ */
    .slm-bot-wrap {
      position: absolute;
      top: calc(100% + 14px);
      z-index: 99999;
      animation: slideInBot .3s cubic-bezier(.34,1.3,.64,1) both;
    }
    .slm-bot-wrap.right { right: 0; }
    .slm-bot-wrap.left  { left: 0; }

    /* Triangle arrow */
    .slm-bot-wrap::before {
      content: '';
      position: absolute;
      top: -9px;
      width: 16px; height: 9px;
      background: #c0001a;
      clip-path: polygon(50% 0%, 100% 100%, 0% 100%);
    }
    .slm-bot-wrap.right::before { right: 24px; }
    .slm-bot-wrap.left::before  { left: 24px;  }

    .slm-bot-card {
      width: 340px;
      background: #fff;
      border-radius: 16px;
      border: 1px solid rgba(0,0,0,0.07);
      overflow: hidden;
      box-shadow:
        0 4px 8px rgba(0,0,0,.06),
        0 18px 52px rgba(0,0,0,.16),
        0 2px 10px rgba(192,0,26,.1);
    }

    /* Bot header */
    .slm-bot-hd {
      background: linear-gradient(135deg, #c0001a 0%, #8b0000 58%, #5c0000 100%);
      padding: clamp(9px,2.6vw,12px) clamp(10px,3vw,14px) clamp(8px,2.4vw,10px);
      display: flex;
      align-items: center;
      gap: clamp(7px,2vw,11px);
      position: relative;
      overflow: visible;
    }
    .slm-bot-hd::after {
      content: '';
      position: absolute; inset: 0;
      pointer-events: none;
      background-image:
        linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px);
      background-size: 16px 16px;
    }
    .slm-bot-av   { position: relative; flex-shrink: 0; z-index: 1; overflow: visible; }
    .slm-bot-av svg { width: clamp(46px,12vw,62px); height: clamp(46px,12vw,62px); display: block; }
    .slm-bot-bars {
      display: flex; align-items: flex-end; gap: 2px;
      position: absolute; bottom: -3px; right: -5px;
      background: rgba(0,0,0,.3); border-radius: 4px;
      padding: 2px 3px; z-index: 2;
    }
    .slm-bot-b { width: 2.5px; border-radius: 2px; background: #fff; transform-origin: bottom; }
    .slm-bot-b:nth-child(1){ height:4px;  animation:speakBar .48s .00s ease-in-out infinite; }
    .slm-bot-b:nth-child(2){ height:9px;  animation:speakBar .48s .10s ease-in-out infinite; }
    .slm-bot-b:nth-child(3){ height:6px;  animation:speakBar .48s .20s ease-in-out infinite; }
    .slm-bot-b:nth-child(4){ height:3px;  animation:speakBar .48s .30s ease-in-out infinite; }
    .slm-bot-meta { flex: 1; z-index: 1; min-width: 0; overflow: hidden; }
    .slm-bot-name {
      font-size: clamp(10px,2.8vw,11.5px); font-weight: 800; color: #fff;
      letter-spacing: .06em; font-family: 'DM Mono','Fira Code',monospace;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .slm-bot-sub  { font-size: clamp(8.5px,2.4vw,9.5px); color: rgba(255,255,255,.7); display: flex; align-items: center; gap: 4px; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .slm-bot-dot  { width: 5px; height: 5px; border-radius: 50%; background: #6effa0; box-shadow: 0 0 4px rgba(100,255,150,.85); flex-shrink: 0; animation: pulseDot 2s ease-in-out infinite; }
    .slm-bot-badge{ z-index:1; flex-shrink:0; background:rgba(255,255,255,.16); border:1px solid rgba(255,255,255,.3); border-radius:20px; padding:2px clamp(6px,2vw,8px); font-size:clamp(9px,2.4vw,10px); font-weight:700; color:#fff; white-space:nowrap; }

    /* Bot tag strip */
    .slm-bot-tag     { background: linear-gradient(90deg,#fff5f5,#fff); border-bottom: 1px solid rgba(192,0,26,.1); padding: 5px clamp(10px,3vw,14px); display: flex; align-items: center; gap: 6px; }
    .slm-bot-tag-dot { width: 5px; height: 5px; border-radius: 50%; background: #c0001a; flex-shrink: 0; }
    .slm-bot-tag-txt { font-size: clamp(8.5px,2.4vw,9.5px); font-weight: 800; color: #c0001a; letter-spacing: .08em; }

    /* Bot body */
    .slm-bot-body { padding: clamp(8px,2.6vw,10px) clamp(10px,3vw,14px) clamp(6px,2vw,8px); }
    .slm-bot-msg  { font-size: clamp(12px,3.4vw,13.5px); line-height: 1.62; color: #1a1a1a; word-break: break-word; margin: 0; }
    .slm-bot-msg strong { color: #c0001a; font-weight: 800; }
    .slm-bot-msg em     { color: #8b0000; font-style: normal; font-weight: 700; }
    .slm-bot-cur  { display: inline-block; width: 2px; height: 13px; background: #c0001a; border-radius: 2px; margin-left: 2px; vertical-align: middle; animation: botCursor .65s ease-in-out infinite; }

    /* Bot footer */
    .slm-bot-ft   { padding: clamp(7px,2.2vw,8px) clamp(10px,3vw,14px) clamp(9px,2.8vw,12px); display: flex; align-items: center; justify-content: space-between; gap: 8px; border-top: 1px solid rgba(0,0,0,.05); background: #fafafa; flex-wrap: wrap; }
    .slm-bot-dots { display: flex; gap: 5px; align-items: center; }
    .slm-bot-d    { width: 7px; height: 7px; border-radius: 50%; background: #e0e0e0; transition: all .3s; flex-shrink: 0; }
    .slm-bot-d.on { width: 20px; border-radius: 999px; background: linear-gradient(90deg,#c0001a,#8b0000); }
    .slm-bot-skip { background: none; border: none; cursor: pointer; font-size: clamp(10px,2.8vw,11px); color: #c0c0c0; padding: 4px 6px; border-radius: 6px; transition: all .18s; white-space: nowrap; }
    .slm-bot-skip:hover { background: #f0f0f0; color: #888; }
    .slm-bot-next { background: linear-gradient(135deg,#c0001a,#8b0000); color: #fff; border: none; border-radius: 8px; padding: 7px clamp(12px,3.6vw,18px); font-size: clamp(11px,3vw,12.5px); font-weight: 800; cursor: pointer; letter-spacing: .03em; box-shadow: 0 3px 12px rgba(192,0,26,.32); transition: all .18s; white-space: nowrap; }
    .slm-bot-next:hover  { transform: translateY(-1px); box-shadow: 0 5px 16px rgba(192,0,26,.46); }
    .slm-bot-next:active { transform: scale(.97); }

    /* ── Hamburger ── */
    .slm-hamburger {
      display: none;
      flex-direction: column;
      gap: 5px;
      cursor: pointer;
      padding: 8px;
      border: none;
      background: none;
      z-index: 9200;
      margin-left: auto;
    }
    .slm-hamburger span {
      display: block;
      width: 24px; height: 2.5px;
      background: #c0001a;
      border-radius: 2px;
      transition: all .3s ease;
    }
    .slm-hamburger.open span:nth-child(1){ transform: rotate(45deg)  translate(5px,5px); }
    .slm-hamburger.open span:nth-child(2){ opacity: 0; }
    .slm-hamburger.open span:nth-child(3){ transform: rotate(-45deg) translate(5px,-5px); }

    /* ── Mobile nav dropdown ── */
    .slm-mobile-nav {
      display: none;
      flex-direction: column;
      position: absolute;
      top: 72px; left: 0; right: 0;
      background: #fff;
      padding: 10px 16px 14px;
      box-shadow: 0 12px 32px rgba(0,0,0,0.1);
      border-radius: 0 0 14px 14px;
      z-index: 9100;
      animation: slideDown .22s ease;
      gap: 2px;
    }
    .slm-mobile-nav.open { display: flex; }
    .slm-mobile-nav a {
      display: block;
      padding: 10px 12px;
      border-radius: 8px;
      font-size: 15px;
      font-family: 'Inter', sans-serif;
      color: #333;
      text-decoration: none;
      transition: background .15s, color .15s;
    }
    .slm-mobile-nav a:hover,
    .slm-mobile-nav a.active { background: rgba(192,0,26,.06); color: #c0001a; }

    /* Mobile auth row inside dropdown */
    .slm-mobile-auth-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px 2px;
      border-top: 1px solid rgba(0,0,0,.06);
      margin-top: 6px;
      /* FIX: anchor point for the bot popup on mobile — the popup is
         positioned absolutely relative to THIS row, so it always
         renders directly beneath the highlighted Login/Signup button
         instead of floating disconnected at the bottom of the screen */
      position: relative;
    }

    /* ── Backdrop ── */
    .slm-backdrop {
      position: fixed; inset: 0;
      background: rgba(0,0,0,.25);
      z-index: 9050;
    }

    /* ── Responsive ── */
    @media (max-width: 991px) {
      .slm-nav       { display: none !important; }
      .slm-auth      { display: none !important; }
      .slm-hamburger { display: flex !important; }
      .slm-logo img  { height: 44px !important; }

      /* FIX: Bot popup — instead of pinning to the viewport (which
         disconnected it from the Login/Signup buttons living inside
         the collapsed hamburger dropdown), anchor it directly below
         the mobile auth row so it visually stays attached to the
         button it's guiding the user to. Width is constrained so it
         never overflows a narrow screen. */
      .slm-bot-wrap {
        position: absolute !important;
        top: calc(100% + 14px) !important;
        left: 16px !important;
        right: 16px !important;
        bottom: auto !important;
        width: auto !important;
      }
      .slm-bot-wrap::before { display: none !important; }
      .slm-bot-card { width: 100% !important; max-width: 380px; margin: 0 auto; }
    }
    @media (max-width: 575px) {
      .slm-logo img  { height: 38px !important; }
      .slm-bot-card  { width: 270px !important; }
      .slm-bot-msg   { font-size: 12.5px !important; }
    }
  `;
  document.head.appendChild(s);
};

/* ═══════════════════════════════════════════════════
   BOT FACE SVG — same as Step4 (bobbing + floating hearts)
═══════════════════════════════════════════════════ */
const BotFaceSVG: React.FC = () => (
  <svg viewBox="0 0 72 72" fill="none" style={{ overflow: "visible" }}>
    <defs>
      <style>{`
        .slm-bot-bob { animation: botBobSvg 3s ease-in-out infinite; }
        .slm-hl1 { animation: floatHeartL1 2s 0.0s  ease-out infinite; transform-origin: 23px 27px; }
        .slm-hl2 { animation: floatHeartL2 2s 0.65s ease-out infinite; transform-origin: 23px 27px; }
        .slm-hl3 { animation: floatHeartL3 2s 1.3s  ease-out infinite; transform-origin: 23px 27px; }
        .slm-hr1 { animation: floatHeartR1 2s 0.2s  ease-out infinite; transform-origin: 49px 27px; }
        .slm-hr2 { animation: floatHeartR2 2s 0.85s ease-out infinite; transform-origin: 49px 27px; }
        .slm-hr3 { animation: floatHeartR3 2s 1.5s  ease-out infinite; transform-origin: 49px 27px; }
      `}</style>
    </defs>
    <g className="slm-bot-bob">
      <line x1="36" y1="10" x2="36" y2="3" stroke="rgba(255,255,255,0.82)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="36" cy="2" r="3.2" fill="rgba(255,255,255,0.9)" />
      <rect x="7" y="10" width="58" height="46" rx="15" fill="rgba(255,255,255,0.97)" />
      <circle cx="7"  cy="32" r="5.5" fill="rgba(255,255,255,0.85)" stroke="rgba(192,0,26,0.45)" strokeWidth="1.2" />
      <circle cx="65" cy="32" r="5.5" fill="rgba(255,255,255,0.85)" stroke="rgba(192,0,26,0.45)" strokeWidth="1.2" />
      <rect x="11" y="17" width="24" height="20" rx="9" fill="#c0001a" />
      <rect x="37" y="17" width="24" height="20" rx="9" fill="#8b0000" />
      <ellipse cx="23" cy="27" rx="9" ry="8" fill="white" />
      <ellipse cx="49" cy="27" rx="9" ry="8" fill="white" />
      <g className="slm-hl1"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#ff3d6b" /></g>
      <g className="slm-hl2"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#c0001a" opacity="0.88" /></g>
      <g className="slm-hl3"><path d="M23,33 C23,33 12,23 12,17 C12,13 17,11 23,17 C29,11 34,13 34,17 C34,23 23,33 23,33Z" fill="#ff7099" /></g>
      <g className="slm-hr1"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#ff3d6b" /></g>
      <g className="slm-hr2"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#c0001a" opacity="0.88" /></g>
      <g className="slm-hr3"><path d="M49,33 C49,33 38,23 38,17 C38,13 43,11 49,17 C55,11 60,13 60,17 C60,23 49,33 49,33Z" fill="#ff7099" /></g>
      <path d="M16,46 Q36,60 56,46" stroke="#c0001a" strokeWidth="2.8" strokeLinecap="round" fill="none" />
      <ellipse cx="12" cy="44" rx="6" ry="4" fill="rgba(255,100,100,0.2)" />
      <ellipse cx="60" cy="44" rx="6" ry="4" fill="rgba(255,100,100,0.2)" />
      <line x1="7"  y1="28" x2="2"  y2="23" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="7"  y1="36" x2="2"  y2="39" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="65" y1="28" x2="70" y2="23" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="65" y1="36" x2="70" y2="39" stroke="rgba(255,255,255,0.78)" strokeWidth="2.4" strokeLinecap="round" />
    </g>
  </svg>
);

/* ═══════════════════════════════════════════════════
   BOT MESSAGE DATA
═══════════════════════════════════════════════════ */
const MSG_DATA = {
  login: {
    tag:   "STEP 1 OF 2 — WELCOME BACK",
    plain: "Already have an account? Hit Login and dive back into your matches right away!",
    html:  <>Already have an account? Hit <strong>Login</strong> and dive back into your matches right away!</>,
  },
  signup: {
    tag:   "STEP 2 OF 2 — GET STARTED",
    plain: "New here? Create your free account in just 30 seconds and find your perfect match today!",
    html:  <>New here? <strong>Create your free account</strong> in just <strong>30 seconds</strong> and find your perfect match today!</>,
  },
} as const;

/* ═══════════════════════════════════════════════════
   BOT POPUP COMPONENT
═══════════════════════════════════════════════════ */
const BotPopup: React.FC<{
  which:     "login" | "signup";
  align:     "left"  | "right";
  onSkip:    () => void;
  onNext:    () => void;
  nextLabel: string;
}> = ({ which, align, onSkip, onNext, nextLabel }) => {
  const [displayed,  setDisplayed]  = useState("");
  const [done,       setDone]       = useState(false);
  const [status,     setStatus]     = useState("typing...");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stepNum = which === "login" ? 1 : 2;
  const data    = MSG_DATA[which];

  useEffect(() => {
    setDisplayed(""); setDone(false); setStatus("typing...");
    let i = 0;
    const tick = () => {
      if (i <= data.plain.length) {
        setDisplayed(data.plain.slice(0, i)); i++;
        timerRef.current = setTimeout(tick, 22);
      } else {
        setDone(true);
        setStatus("Online — here to guide you!");
      }
    };
    tick();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [which]);

  return (
    <div className={`slm-bot-wrap ${align}`}>
      <div className="slm-bot-card">

        {/* Header */}
        <div className="slm-bot-hd">
          <div className="slm-bot-av">
            <BotFaceSVG />
            <div className="slm-bot-bars">
              <div className="slm-bot-b"/><div className="slm-bot-b"/>
              <div className="slm-bot-b"/><div className="slm-bot-b"/>
            </div>
          </div>
          <div className="slm-bot-meta">
            <div className="slm-bot-name">SLAMMMM GUIDE</div>
            <div className="slm-bot-sub"><div className="slm-bot-dot"/><span>{status}</span></div>
          </div>
          <div className="slm-bot-badge">{stepNum} / 2</div>
        </div>

        {/* Tag */}
        <div className="slm-bot-tag">
          <div className="slm-bot-tag-dot"/>
          <div className="slm-bot-tag-txt">{data.tag}</div>
        </div>

        {/* Message */}
        <div className="slm-bot-body">
          {done
            ? <p className="slm-bot-msg">{data.html}</p>
            : <p className="slm-bot-msg">{displayed}<span className="slm-bot-cur"/></p>
          }
        </div>

        {/* Footer */}
        <div className="slm-bot-ft">
          <div className="slm-bot-dots">
            <div className={`slm-bot-d${stepNum === 1 ? " on" : ""}`}/>
            <div className={`slm-bot-d${stepNum === 2 ? " on" : ""}`}/>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="slm-bot-skip" onClick={onSkip}>Skip guide</button>
            <button
              className="slm-bot-next"
              onClick={onNext}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "none"; }}
            >{nextLabel}</button>
          </div>
        </div>

      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   HEADER — MAIN COMPONENT
═══════════════════════════════════════════════════ */
const Header: React.FC = () => {
  const [isScrolled,           setIsScrolled]           = useState(false);
  const [guideStep,            setGuideStep]            = useState<GuideStep>(null);
  const [mobileMenuOpen,       setMobileMenuOpen]       = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [mobileLocationOpen,   setMobileLocationOpen]   = useState(false);

  const locationDropdownRef = useRef<HTMLLIElement>(null);

  const location        = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const isActive        = (path: string) => location.pathname === path ? "active" : "";
  const isLocationActive = ["/florida", "/orlando", "/new-york", "/michigan", "/alaska", "/north-carolina", "/texas", "/california", "/hawaii"].includes(location.pathname) ? "active" : "";

  /* Close mobile menu on route change */
  useEffect(() => {
    setMobileMenuOpen(false);
    setLocationDropdownOpen(false);
    setMobileLocationOpen(false);
  }, [location.pathname]);

  /* Close Location dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target as Node)) {
        setLocationDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    injectStyles();
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);

    /* Show bot guide only once */
    if (!localStorage.getItem("slmHeaderGuide") && !isAuthenticated) {
      setTimeout(() => {
        setGuideStep("login");
        localStorage.setItem("slmHeaderGuide", "1");
      }, 900);
    }
    return () => window.removeEventListener("scroll", onScroll);
  }, [isAuthenticated]);

  /* Close mobile menu on desktop resize */
  useEffect(() => {
    const hr = () => { if (window.innerWidth >= 992) setMobileMenuOpen(false); };
    window.addEventListener("resize", hr);
    return () => window.removeEventListener("resize", hr);
  }, []);

  /* On mobile, the Login/Signup buttons the guide points at live inside
     the collapsed hamburger dropdown — open it for the guide's duration
     so the highlighted button + popup are actually visible, and close it
     again once the guide finishes (skip / done) */
  useEffect(() => {
    if (window.innerWidth >= 992) return;
    setMobileMenuOpen(!!guideStep);
  }, [guideStep]);

  const handleNext = useCallback(
    () => setGuideStep(prev => prev === "login" ? "signup" : null),
    []
  );
  const handleSkip = useCallback(() => setGuideStep(null), []);

  /* Auth buttons — reusable for desktop + mobile */
  const renderAuth = (align: "left" | "right") => {
    if (isAuthenticated) {
      return (
        <Link className="slm-dashboard-btn" to="/dashboard">
          <i className="fa-solid fa-gauge-high" style={{ fontSize: 13 }}/> Dashboard
        </Link>
      );
    }
    return (
      <>
        {/* Login */}
        <div
          className={`slm-anchor${guideStep === "login" ? " glow" : ""}`}
          style={{ zIndex: guideStep === "login" ? 9500 : 1 }}
        >
          <Link className="slm-login-btn" to="/login">
            <i className="fa-regular fa-user"/> Login
          </Link>
          {guideStep === "login" && (
            <BotPopup
              which="login"
              align={align}
              onSkip={handleSkip}
              onNext={handleNext}
              nextLabel="Next →"
            />
          )}
        </div>

        {/* Sign Up */}
        <div
          className={`slm-anchor${guideStep === "signup" ? " glow" : ""}`}
          style={{ zIndex: guideStep === "signup" ? 9500 : 1 }}
        >
          <Link className="slm-signup-btn" to="/signup">
            Sign Up
          </Link>
          {guideStep === "signup" && (
            <BotPopup
              which="signup"
              align={align}
              onSkip={handleSkip}
              onNext={handleSkip}
              nextLabel="Done ✓"
            />
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {/* ── Dark overlay when bot is showing ── */}
      {guideStep && (
        <div
          style={{
            position: "fixed", inset: 0,
            background: "rgba(8,0,0,0.52)",
            zIndex: 9000,
            animation: "fadeInOverlay .28s ease",
            cursor: "pointer",
          }}
          onClick={handleSkip}
        />
      )}

      {/* ══ HEADER ══ */}
      <header className={`slm-header${isScrolled ? " scrolled" : ""}`}>
        <div className="container">
          <div className="slm-header-inner">

            {/* Logo */}
            <Link className="slm-logo" to="/">
              <img src={logo} alt="Slammm" />
            </Link>

            {/* Desktop nav links */}
            <ul className="slm-nav">
              <li><Link className={`slm-nav-link ${isActive("/mission")}`}  to="/mission">Mission</Link></li>
              <li><Link className={`slm-nav-link ${isActive("/features")}`} to="/features">Features</Link></li>
              <li><Link className={`slm-nav-link ${isActive("/blogs")}`}    to="/blogs">Blogs</Link></li>
              <li><Link className={`slm-nav-link ${isActive("/slammmm-love-stories")}`}    to="/slammmm-love-stories">Stories</Link></li>
              <li className="slm-nav-dropdown" ref={locationDropdownRef}>
                <button
                  type="button"
                  className={`slm-nav-dropdown-toggle ${isLocationActive}`}
                  onClick={() => setLocationDropdownOpen(p => !p)}
                  aria-expanded={locationDropdownOpen}
                >
                  Location
                  <i className={`fa-solid fa-chevron-down slm-nav-caret${locationDropdownOpen ? " open" : ""}`}/>
                </button>
                <div className={`slm-nav-dropdown-menu${locationDropdownOpen ? " open" : ""}`}>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/florida")}`}
                    to="/florida"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Florida
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/orlando")}`}
                    to="/orlando"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Orlando
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/new-york")}`}
                    to="/new-york"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    New York
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/michigan")}`}
                    to="/michigan"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Michigan
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/alaska")}`}
                    to="/alaska"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Alaska
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/north-carolina")}`}
                    to="/north-carolina"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    North Carolina
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/texas")}`}
                    to="/texas"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Texas
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/california")}`}
                    to="/california"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    California
                  </Link>
                  <Link
                    className={`slm-nav-dropdown-item ${isActive("/hawaii")}`}
                    to="/hawaii"
                    onClick={() => setLocationDropdownOpen(false)}
                  >
                    Hawaii
                  </Link>
                </div>
              </li>
              <li><Link className={`slm-nav-link ${isActive("/help")}`}     to="/help">Help</Link></li>
              <li><Link className={`slm-nav-link ${isActive("/contact-dating-agency")}`}  to="/contact-dating-agency">Contact Us</Link></li>
            </ul>

            {/* Desktop auth buttons */}
            <div className="slm-auth">
              {renderAuth("right")}
            </div>

            {/* Hamburger — mobile only */}
            <button
              className={`slm-hamburger${mobileMenuOpen ? " open" : ""}`}
              onClick={() => setMobileMenuOpen(p => !p)}
              aria-label="Toggle menu"
            >
              <span/><span/><span/>
            </button>

          </div>
        </div>

        {/* Mobile dropdown nav */}
        <div className={`slm-mobile-nav${mobileMenuOpen ? " open" : ""}`}>
          <Link className={isActive("/mission")}  to="/mission"  onClick={() => setMobileMenuOpen(false)}>Mission</Link>
          <Link className={isActive("/features")} to="/features" onClick={() => setMobileMenuOpen(false)}>Features</Link>
          <Link className={isActive("/blogs")}    to="/blogs"    onClick={() => setMobileMenuOpen(false)}>Blogs</Link>
          <Link className={isActive("/slammmm-love-stories")}    to="/slammmm-love-stories"    onClick={() => setMobileMenuOpen(false)}>Stories</Link>
          <div className="slm-mobile-nav-dropdown">
            <button
              type="button"
              className={`slm-mobile-dropdown-toggle ${isLocationActive}`}
              onClick={() => setMobileLocationOpen(p => !p)}
              aria-expanded={mobileLocationOpen}
            >
              Location
              <i className={`fa-solid fa-chevron-down${mobileLocationOpen ? " open" : ""}`}/>
            </button>
            {mobileLocationOpen && (
              <div className="slm-mobile-dropdown-menu">
                <Link className={isActive("/florida")} to="/florida" onClick={() => setMobileMenuOpen(false)}>Florida</Link>
                <Link className={isActive("/orlando")} to="/orlando" onClick={() => setMobileMenuOpen(false)}>Orlando</Link>
                <Link className={isActive("/new-york")} to="/new-york" onClick={() => setMobileMenuOpen(false)}>New York</Link>
                <Link className={isActive("/michigan")} to="/michigan" onClick={() => setMobileMenuOpen(false)}>Michigan</Link>
                <Link className={isActive("/alaska")} to="/alaska" onClick={() => setMobileMenuOpen(false)}>Alaska</Link>
                <Link className={isActive("/north-carolina")} to="/north-carolina" onClick={() => setMobileMenuOpen(false)}>North Carolina</Link>
                <Link className={isActive("/texas")} to="/texas" onClick={() => setMobileMenuOpen(false)}>Texas</Link>
                <Link className={isActive("/california")} to="/california" onClick={() => setMobileMenuOpen(false)}>California</Link>
                <Link className={isActive("/hawaii")} to="/hawaii" onClick={() => setMobileMenuOpen(false)}>Hawaii</Link>
              </div>
            )}
          </div>
          <Link className={isActive("/help")}     to="/help"     onClick={() => setMobileMenuOpen(false)}>Help</Link>
          <Link className={isActive("/contact-dating-agency")}  to="/contact-dating-agency"  onClick={() => setMobileMenuOpen(false)}>Contact Us</Link>
          <div className="slm-mobile-auth-row">
            {renderAuth("left")}
          </div>
        </div>
      </header>

      {/* Spacer so content doesn't hide behind fixed header */}
      <div style={{ height: 72 }} />

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div className="slm-backdrop" onClick={() => setMobileMenuOpen(false)} />
      )}
    </>
  );
};

export default Header;